import { useEffect, useRef } from 'react';

import type { PaletteCommand } from './commandPalette.types';
import { NAV_THEMES, type NavTheme } from './nav-tokens';

interface PaletteRowProps {
  command: PaletteCommand;
  optionId: string;
  active: boolean;
  theme: NavTheme;
  onRun: () => void;
  onHover: () => void;
}

/** One row in the palette's result list. */
export default function PaletteRow({
  command,
  optionId,
  active,
  theme,
  onRun,
  onHover,
}: PaletteRowProps) {
  const t = NAV_THEMES[theme];
  const Icon = command.icon;
  const ref = useRef<HTMLDivElement>(null);

  // Arrowing past the bottom of the visible list keeps the highlight in view.
  useEffect(() => {
    if (active) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  return (
    <div
      ref={ref}
      id={optionId}
      role="option"
      aria-selected={active}
      // Keeps focus in the search box; the click below does the work.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onRun}
      onMouseMove={onHover}
      className="flex items-center w-full"
      style={{
        gap: 12,
        padding: command.indent ? '7px 16px 7px 42px' : '10px 16px',
        background: active ? t.surface : 'transparent',
        cursor: 'pointer',
      }}
    >
      <Icon
        size={command.indent ? 12 : 14}
        style={{ color: t.subtle, flexShrink: 0 }}
        aria-hidden
      />
      <span className="flex min-w-0 flex-col" style={{ flex: 1 }}>
        <span className="truncate" style={{ fontSize: command.indent ? 12.5 : 13, color: t.ink }}>
          {command.label}
        </span>
        {command.detail && (
          <span className="truncate" style={{ fontSize: 11.5, color: t.muted }}>
            {command.detail}
          </span>
        )}
      </span>
      {command.shortcut && command.shortcut.length > 0 && (
        // One kbd chip per keystroke (Q3) — denotes distinct keypresses.
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {command.shortcut.map((key, i) => (
            <kbd
              key={`${key}-${i}`}
              style={{
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: 10,
                padding: '2px 5px',
                borderRadius: 4,
                background: t.surface,
                color: t.subtle,
              }}
            >
              {key}
            </kbd>
          ))}
        </span>
      )}
      {command.hint && (
        <span style={{ fontSize: 10.5, color: t.faint, flexShrink: 0 }}>{command.hint}</span>
      )}
    </div>
  );
}
